import * as encodingUtils from '@ardoq/html';
import {
  type GraphViewerInputMode,
  type IModelItem,
  type QueryItemToolTipEventArgs,
  INode,
  ILabel,
  type IEdge,
} from '@ardoq/yfiles';
import type { GraphItem, YGraphState } from '@ardoq/graph';
import { HORIZONTAL_ELLIPSIS } from '@ardoq/global-consts';
import { UrlLabelStyle } from 'yfilesExtensions/styles/UrlLabelStyle';
import { isCurrentlyHoveredImageBroken$ } from './isCurrentlyHoveredImageBroken$';
import { CanvasOtherLabelStyle } from 'tabview/blockDiagram/view/yFilesExtensions/labels/CanvasOtherLabelStyle';
import { graphItemTooltipText } from './graphItemTooltipText';
import { INVISIBLE_SEPARATOR } from 'tabview/blockDiagram/view/yFilesExtensions/modernized/consts';
import {
  getUrlFieldValuesPopoverState,
  type SetUrlFieldValuesPopover,
} from './getUrlFieldValuesPopoverState';
import { ArdoqURLLabelStyle } from 'yfilesExtensions/styles/ardoqURLLabelStyle';
import { WorkspaceModeOpenGroupUrlLabelStyle } from 'yfilesExtensions/styles/WorkspaceModeOpenGroupUrlLabelStyle';
import { CanvasLabelStyle } from 'tabview/blockDiagram/view/yFilesExtensions/labels/CanvasLabelStyle';
import { CollapsedComponentGroupLabelStyle } from 'tabview/blockDiagram/view/yFilesExtensions/modernized/CollapsedComponentGroupLabelStyle';
import { wrapLeafNodeLabel } from 'tabview/blockDiagram/view/yFilesExtensions/modernized/graphBuilder/wrapLeafNodeLabel';
import { GroupLabelStyle } from 'tabview/blockDiagram/view/yFilesExtensions/modernized/groupLabels/GroupLabelStyle';
import { wrapString } from 'tabview/blocks/misc/string';
import { measureNodeLabel } from '@ardoq/graph-labeling';
import { MODERNIZED_BLOCK_DIAGRAM_LABEL_MAX_LINES as MAX_LINES } from 'yfilesExtensions/styles/modernized/consts';

type TooltipListenerResult = [
  destroyPopover: VoidFunction,
  listener: (
    e: QueryItemToolTipEventArgs<IModelItem>,
    inputMode: GraphViewerInputMode
  ) => void,
];

const endsWithEllipsis = (lines: string[]): boolean =>
  Boolean(lines.at(-1)?.endsWith(HORIZONTAL_ELLIPSIS));

const getWrappedLabel = (
  item: INode,
  leafNodeLayout: YGraphState['leafNodeLayout'],
  groupHeaders: YGraphState['groupHeaders']
): string[] | undefined => {
  const { id } = item.tag as GraphItem;
  const leafLayout = leafNodeLayout.get(id);
  if (leafLayout) return leafLayout.wrappedMainLabel;

  const groupHeaderEntry = groupHeaders.get(id);
  if (groupHeaderEntry) {
    const [, layout] = groupHeaderEntry;
    if (endsWithEllipsis(layout.wrappedSubLabel)) return layout.wrappedSubLabel;
    return layout.wrappedMainLabel;
  }
  // Collapsed groups are absent from both leafNodeLayout and groupHeaders.
  // We need to re-derive truncation using the same wrap params as the label style.
  const collapsedComponentLabel = item.labels.find(
    label => label.style === CollapsedComponentGroupLabelStyle.Instance
  );

  if (collapsedComponentLabel) {
    return wrapLeafNodeLabel(collapsedComponentLabel.text);
  }

  // Collapsed non-component groups are absent from groupHeaders
  // because layoutGroupHeader returns null for any collapsed group. GroupLabelStyle
  // wraps using the label's rendered width and we replicate that here.
  const collapsedGroupLabel = item.labels.find(
    label => label.style === GroupLabelStyle.Instance
  );
  if (collapsedGroupLabel) {
    return wrapString(
      collapsedGroupLabel.text,
      collapsedGroupLabel.layout.width,
      measureNodeLabel,
      MAX_LINES
    );
  }
  return undefined;
};

const modernizedTooltipListener = (
  setUrlFieldValuesPopover: SetUrlFieldValuesPopover
): TooltipListenerResult => {
  const destroyPopover = () => setUrlFieldValuesPopover(null);

  return [
    destroyPopover,
    (
      e: QueryItemToolTipEventArgs<IModelItem>,
      inputMode: GraphViewerInputMode
    ) => {
      const { graph } = inputMode;
      const {
        item,
        context: { canvasComponent },
      } = e;

      if (!item?.tag) {
        return;
      }

      const isLabel = item instanceof ILabel;
      const isUrlLabel =
        isLabel &&
        (item.style === UrlLabelStyle.Instance ||
          item.style === ArdoqURLLabelStyle.Instance ||
          item.style === WorkspaceModeOpenGroupUrlLabelStyle.Instance);

      if (isUrlLabel) {
        const popoverState = getUrlFieldValuesPopoverState(
          canvasComponent,
          item
        );
        if (popoverState) {
          setUrlFieldValuesPopover(popoverState);
        }
        e.handled = true;
        return;
      }

      if (
        isLabel &&
        item.style instanceof CanvasOtherLabelStyle &&
        graph?.tag
      ) {
        const { edgeOtherLabels } = graph.tag as YGraphState;
        const { text, owner } = item;
        const { tag } = owner as IEdge | INode;
        const { id } = tag as GraphItem;
        const otherLabels = edgeOtherLabels.get(id);
        if (!otherLabels?.length) {
          return;
        }
        if (!otherLabels.some(endsWithEllipsis)) {
          return;
        }
        e.toolTip = text.replaceAll(INVISIBLE_SEPARATOR, '\r\n\r\n');
        return;
      }

      if (isLabel && item.style === CanvasLabelStyle.Instance && graph?.tag) {
        const { edgeLabels } = graph.tag as YGraphState;
        const { text, owner } = item;
        const { tag } = owner as IEdge | INode;
        const { id } = tag as GraphItem;
        const mainLabel = edgeLabels.get(id);
        if (!mainLabel || !endsWithEllipsis(mainLabel)) {
          return;
        }
        e.toolTip = text;
        return;
      }

      if (!(item instanceof INode)) {
        return;
      }

      const {
        state: { isCurrentlyHoveredImageBroken },
      } = isCurrentlyHoveredImageBroken$;
      if (isCurrentlyHoveredImageBroken) {
        return;
      }

      if (graph?.tag) {
        const { leafNodeLayout, groupHeaders } = graph.tag as YGraphState;
        const wrappedLabel = getWrappedLabel(
          item,
          leafNodeLayout,
          groupHeaders
        );

        if (wrappedLabel !== undefined && !endsWithEllipsis(wrappedLabel)) {
          e.handled = true; // stops yFiles from bubbling up to the parent group
          return;
        }
      }

      const toolTipText = graphItemTooltipText(item, -Infinity, true);
      e.toolTip = toolTipText ? encodingUtils.escapeHTML(toolTipText) : null;
    },
  ];
};
export { modernizedTooltipListener };
